import React, { useCallback, useState } from 'react';
import clsx from 'clsx';
import { useDispatch } from 'react-redux';
import { Redirect, useLocation } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import {
  Grid,
  TextField,
  Button,
  Typography,
  InputAdornment,
  Link,
  Paper,
} from '@mui/material';
import { makeStyles, createStyles } from '@mui/styles';
import { useTypedSelector as useSelector } from '../../_redux/reducers';
import { initiateSignIn, completeSignIn } from '../../_redux/auth';
import { getAuthToken } from '../../_redux/auth/selectors';

// import Path954 from '../../assets/path954.svg';

const useStyles = makeStyles((theme) =>
  createStyles({
    paperRoot: {
      marginTop: theme.spacing(2),
      padding: theme.spacing(2),
      // backgroundColor: '#f8e1db',
      // backgroundImage: `url(${Path954})`,
      // backgroundPosition: '120%',
      // backgroundRepeat: 'no-repeat',
    },
    marginTop: {
      marginTop: theme.spacing(1),
    },
    center: {
      display: 'flex',
      justifyContent: 'center',
    },
    textField: {
      width: 250,
      maxWidth: '100%',
    },
    link: {
      cursor: 'pointer',
    },
    titleText: {
      fontWeight: theme.typography.fontWeightMedium,
      marginBottom: theme.spacing(4),
    },
  }),
);

const Login: React.FC = () => {
  const classes = useStyles();
  const { search } = useLocation();
  const dispatch: AppDispatch = useDispatch();

  const accessToken = useSelector(getAuthToken);

  const [codeSentAt, setCodeSentAt] = useState(0);
  const [error, setError] = useState<null | StringKeyObject>(null);
  const editPhone = useCallback(() => setCodeSentAt(0), []);

  const loginForm = useFormik({
    initialValues: { phone: '', verificationCode: '' },
    validationSchema: Yup.object().shape({
      phone:
        // prettier-ignore
        process.env.NODE_ENV === 'production'
          ? Yup.string().required('Phone number is required').matches(/^[0-9]{10}$/, 'Invalid phone number')
          : Yup.string().required('Phone number is required'),
      verificationCode: codeSentAt
        ? Yup.string().required('Code is required')
        : Yup.string().notRequired(),
    }),
    onSubmit: async (values) => {
      setError(null);
      if (codeSentAt) {
        const error = await dispatch(
          completeSignIn(values.phone, values.verificationCode),
        );
        if (error) setError(error);
      } else {
        const [response, success] = await dispatch(
          initiateSignIn(values.phone),
        );
        if (success) setCodeSentAt(Date.now());
        else setError(response);
      }
    },
  });

  const {
    errors: formErrors,
    touched,
    values,
    getFieldProps,
    isSubmitting,
  } = loginForm;
  const getError = (field: keyof typeof loginForm.initialValues) =>
    !!(formErrors[field] && touched[field]);
  const getHelperText = (field: keyof typeof loginForm.initialValues) =>
    touched[field] && formErrors[field];

  if (accessToken)
    return <Redirect to={new URLSearchParams(search).get('nextUrl') || '/'} />;

  const renderPhoneForm = () => (
    <form onSubmit={loginForm.handleSubmit}>
      <Grid container spacing={1}>
        <Grid item xs={12} className={classes.center}>
          <TextField
            autoFocus
            required
            className={classes.textField}
            variant="outlined"
            label="Phone Number"
            placeholder="10 Digit Phone Number"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">+1</InputAdornment>
              ),
            }}
            error={getError('phone')}
            helperText={getHelperText('phone')}
            {...getFieldProps('phone')}
          />
        </Grid>
        <Grid item xs={12} className={classes.center}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            disabled={isSubmitting}
          >
            Get Verification Code
          </Button>
        </Grid>
      </Grid>
    </form>
  );
  const renderPhoneDisplay = () => (
    <div>
      <Typography align="center" color="textSecondary">
        Code sent to
      </Typography>
      <Typography align="center" variant="h5">
        {values.phone}
      </Typography>
      <Typography align="center" color="textSecondary">
        <Link onClick={editPhone} className={classes.link} underline="hover">
          Change Phone
        </Link>
      </Typography>
    </div>
  );

  const renderVerificationForm = () => (
    <form onSubmit={loginForm.handleSubmit}>
      <Grid container justifyContent="center" alignItems="center" spacing={1}>
        <Grid item xs={12} className={clsx(classes.marginTop, classes.center)}>
          <TextField
            required
            autoFocus
            className={classes.textField}
            variant="outlined"
            label="Verification Code"
            placeholder="Enter Verification code"
            error={getError('verificationCode')}
            helperText={getHelperText('verificationCode')}
            {...getFieldProps('verificationCode')}
          />
        </Grid>
        <Grid item xs={12} className={clsx(classes.marginTop, classes.center)}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            disabled={isSubmitting}
          >
            Verify Phone
          </Button>
        </Grid>
      </Grid>
    </form>
  );

  const renderVerifySuccess = () => (
    <div>
      <Typography
        align="center"
        variant="h6"
        className={classes.marginTop}
        color="textSecondary"
      >
        Phone Verified Successfully
      </Typography>
    </div>
  );

  return (
    <Paper className={classes.paperRoot}>
      <Typography
        align="center"
        variant="h5"
        color="textPrimary"
        className={classes.titleText}
      >
        Sign In
      </Typography>
      {!accessToken && !codeSentAt ? renderPhoneForm() : renderPhoneDisplay()}
      {!!(codeSentAt && !accessToken) && renderVerificationForm()}
      {accessToken && renderVerifySuccess()}
      {!!error && (
        <Typography
          variant="body2"
          align="center"
          color="error"
          className={classes.marginTop}
        >
          {error.message ?? error.error}
        </Typography>
      )}
    </Paper>
  );
};

export default React.memo(Login);
